import React from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import usePatientSearch from "../../hooks/usePatientSearch";
import { usePatientContext } from "../../context/PatientContext";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";

const NAVY = "#1d2a4d";
const TEAL = "#0f9b8e";

/** Rounded, themed dropdown panel for the search results. */
const SearchPaper = (props) => (
  <Paper
    {...props}
    elevation={0}
    sx={{
      mt: 1,
      borderRadius: "12px",
      border: "1px solid #e3e8ef",
      boxShadow: "0 12px 32px rgba(16, 24, 40, 0.16)",
      overflow: "hidden",
      "& .MuiAutocomplete-listbox": { padding: 0 },
      "& .MuiAutocomplete-option": {
        px: 1.5,
        py: 1,
        '&[aria-selected="true"], &.Mui-focused': {
          backgroundColor: "#f2f8f7",
        },
      },
      "& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading": {
        fontSize: "0.85rem",
        color: "#667085",
      },
    }}
  />
);

/**
 * Global patient search, mounted in the navbar top bar. Selecting a result sets
 * the active patient for the whole application (see PatientContext).
 */
export default function NavbarPatientSearch() {
  const { patientList, loading, onInputChange, reset } = usePatientSearch();
  const { patient, selectPatient } = usePatientContext();

  const handleSelect = (event, option) => {
    if (!option) return;
    selectPatient(option);
    reset();
  };

  const getPatientLabel = (option) => {
    const name = displayPatientNameFromParts(option, "Unknown");
    const id = option.patient_id || "";
    const phone = option.phone_no || "";
    return `${name} | ${id}${phone ? ` | ${phone}` : ""}`;
  };

  const hasActive = Boolean(patient?.patient_id);
  const placeholder = hasActive
    ? `Active: ${displayPatientNameFromParts(patient, "Patient")}`
    : "Search patient by name, ID or phone";

  return (
    <Autocomplete
      options={patientList}
      loading={loading}
      value={null}
      blurOnSelect
      clearOnBlur
      selectOnFocus
      handleHomeEndKeys
      openOnFocus
      PaperComponent={SearchPaper}
      getOptionLabel={getPatientLabel}
      isOptionEqualToValue={(opt, val) => opt.patient_id === val?.patient_id}
      onInputChange={onInputChange}
      onChange={handleSelect}
      filterOptions={(options) => options}
      noOptionsText="No patients found"
      loadingText="Searching…"
      renderOption={(props, option) => (
        <li {...props} key={option.patient_id}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Avatar
              sx={{
                bgcolor: NAVY,
                width: 36,
                height: 36,
                fontSize: "0.9rem",
              }}
            >
              <PersonIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "#101828",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayPatientNameFromParts(option, "Unknown")}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#667085" }}>
                {option.patient_id}
                {option.phone_no ? ` · ${option.phone_no}` : ""}
              </Typography>
            </Box>
            {option.gender ? (
              <Chip
                label={option.gender}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  color: TEAL,
                  backgroundColor: "#e6f6f4",
                }}
              />
            ) : null}
          </Box>
        </li>
      )}
      sx={{ width: { xs: "100%", sm: 300, md: 380 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              borderRadius: "12px",
              color: NAVY,
              paddingLeft: "12px",
              fontSize: "0.9rem",
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: "transparent" },
              "&.Mui-focused": {
                boxShadow: `0 0 0 3px ${TEAL}55`,
              },
              "&.Mui-focused fieldset": { borderColor: TEAL },
            },
            "& .MuiInputBase-input": {
              padding: "8px 6px",
              "&::placeholder": {
                color: hasActive ? TEAL : "#98a2b3",
                opacity: 1,
                fontWeight: hasActive ? 600 : 400,
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <SearchIcon sx={{ color: NAVY, mr: 0.5, fontSize: 20 }} />
            ),
            endAdornment: (
              <>
                {loading && (
                  <CircularProgress sx={{ color: NAVY }} size={18} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
